import React, {FC, PropsWithChildren, useCallback, useMemo} from 'react';

import {DataEventContextKey} from '../../../../libs/shared/core/src/models/tracking/dataEventContexts';
import {
  InteractionTrackingContext,
  InteractionTrackingContextType,
  useInteractionTracking,
} from '../../../../libs/shared/ui/src/interactionTracking/interactionTrackingContext';

/*
 * Props.
 */

export interface InteractionTrackingParentProps {
  /** The interaction context that will be added to all events data. */
  interactionContext: DataEventContextKey;
  /** The interaction object that will be added to all events data. */
  interactionObject: string;
  /** The extra data that will be sent with interaction tracking events. */
  trackingData?: Record<string, unknown>;
}

/**
 * Used to add tracking data to all subtree elements.
 *
 * Every child using the integration context will also send the data provided by the `trackingData` prop,
 * as well as the correct interactionContext and interactionObject.
 */
export const InteractionTrackingParent: FC<PropsWithChildren<InteractionTrackingParentProps>> = ({
  children,
  trackingData,
  interactionContext,
  interactionObject,
}) => {
  const trackers = useInteractionTracking();

  const addParentTrackingData = useCallback(
    (extraData: Record<string, unknown> = {}) => ({
      ...trackingData,
      interaction_context: interactionContext,
      interaction_object: interactionObject,
      // Data from child tracking parents should override the current data.
      ...extraData,
    }),
    [trackingData, interactionObject, interactionContext],
  );

  const trackersWithExtraData = useMemo<InteractionTrackingContextType>(
    () => ({
      trackInteraction: (type, id, parentIds, extraData) =>
        trackers.trackInteraction(type, id, parentIds, addParentTrackingData(extraData)),
      trackContextInteraction: (type, context, interactionId, extraData) =>
        trackers.trackContextInteraction(type, context, interactionId, addParentTrackingData(extraData)),
      trackComponentInteraction: (type, context, props, event, extraData) =>
        trackers.trackComponentInteraction(type, context, props, event, addParentTrackingData(extraData)),
    }),
    [trackers, addParentTrackingData],
  );

  return (
    <InteractionTrackingContext.Provider value={trackersWithExtraData}>
      {children}
    </InteractionTrackingContext.Provider>
  );
};
